// Inline-SVG fractal-noise overlay for adding "grainy gradient" texture
// over any coloured surface. Ported from the visionaries-summit branch
// (`components/visionaries-summit/grain-overlay.tsx`) and generalised so
// every dial — frequency, octaves, contrast, blend, opacity, tile size —
// is a prop. Drop it as the first child of a positioning context; the
// parent should `overflow-hidden` if its bounds are rounded.

import { cn } from "@/lib/utils";

export type GrainBlendMode =
  | "normal"
  | "multiply"
  | "screen"
  | "overlay"
  | "darken"
  | "lighten"
  | "color-dodge"
  | "color-burn"
  | "hard-light"
  | "soft-light"
  | "difference"
  | "exclusion"
  | "hue"
  | "saturation"
  | "color"
  | "luminosity"
  | "plus-lighter";

export interface GrainOverlayProps {
  /**
   * Noise frequency. Higher → finer grain (more pixels of noise per tile).
   * 0.6 = soft film grain, 0.9 = standard, 1.4 = sharp dust.
   */
  baseFrequency?: number;
  /** Number of detail layers. 1 = pure, 2 = standard, 3 = busy. */
  numOctaves?: number;
  /**
   * Contrast multiplier applied after desaturation. Centred around 0.5
   * (offset = -contrast/2) so the histogram stretches symmetrically.
   * 1 = no change, 2.2 = matches visionaries-summit, 3 = punchy.
   */
  contrast?: number;
  /** Overlay opacity (0–1). Default 0.8. */
  opacity?: number;
  /** Blend mode against the parent. Default `overlay`. */
  blendMode?: GrainBlendMode;
  /** Tile size in CSS pixels. Default 240. */
  tileSize?: number;
  className?: string;
}

function buildGrainUrl(
  baseFrequency: number,
  numOctaves: number,
  contrast: number,
  tileSize: number
): string {
  const offset = -contrast / 2;
  const matrix = [
    [contrast, 0, 0, 0, offset],
    [0, contrast, 0, 0, offset],
    [0, 0, contrast, 0, offset],
    [0, 0, 0, 1, 0],
  ]
    .flat()
    .join(" ");

  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${tileSize}' height='${tileSize}'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='${baseFrequency}' numOctaves='${numOctaves}' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/><feColorMatrix values='${matrix}'/></filter><rect width='100%' height='100%' filter='url(#n)'/></svg>`;

  return `url("data:image/svg+xml;utf8,${svg.replace(/#/g, "%23")}")`;
}

export function GrainOverlay({
  baseFrequency = 0.9,
  numOctaves = 2,
  contrast = 2.2,
  opacity = 0.8,
  blendMode = "overlay",
  tileSize = 240,
  className,
}: GrainOverlayProps) {
  return (
    <div
      aria-hidden
      style={{
        backgroundImage: buildGrainUrl(
          baseFrequency,
          numOctaves,
          contrast,
          tileSize
        ),
        mixBlendMode: blendMode,
        opacity,
      }}
      className={cn("pointer-events-none absolute inset-0", className)}
    />
  );
}
